import type { AvailableIconName } from '@/assets/icons';
import { Stack } from '@/components/layout';
import { Image, SvgIcon, Text } from '@/components/ui';
import { createStyles, useScaling } from '@/hooks';
import { colors, ColorToken } from '@/theme/colors';
import React, { memo } from 'react';
import { Platform, TouchableOpacity } from 'react-native';

export const SkillAchievedPanel = memo(
  ({
    title,
    buttonLabel,
    icon,
    pinUrl,
    levelColor,
    onPress,
  }: {
    level: number;
    title: string;
    buttonLabel: string;
    icon: AvailableIconName;
    pinUrl?: string | null;
    levelColor: ColorToken;
    onPress: () => void;
  }) => {
    const { scale, isTablet } = useScaling();
    const styles = useStyles();

    return (
      <Stack
        direction="row"
        align="center"
        paddingY={scale(6)}
        paddingLeft={scale({ phone: 60, tablet: 110 })}
        paddingRight={scale(14)}
        gap={scale(24)}
        backgroundColor="white">
        <Stack style={[styles.iconWrap, { borderColor: colors[levelColor] }]}>
          {pinUrl ? (
            <Image source={{ uri: pinUrl }} style={styles.pinImage} resizeMode="contain" />
          ) : (
            <SvgIcon name={icon} size={scale(44)} color={colors[levelColor]} />
          )}
        </Stack>

        <Stack flex={1} gap={scale(10)}>
          <Text
            fontFamily="Inter"
            fontSize={scale({ phone: 12, tablet: 14 })}
            fontWeight="700"
            lineHeight={scale(18)}
            style={{ color: colors[levelColor], maxWidth: isTablet ? undefined : scale(130) }}>
            {title}
          </Text>

          <TouchableOpacity
            activeOpacity={0.85}
            onPress={onPress}
            style={[styles.nextBtn, { backgroundColor: colors[levelColor] }]}>
            <Text
              fontFamily="Inter"
              fontSize={scale(10)}
              fontWeight="400"
              style={styles.nextBtnText}>
              {buttonLabel}
            </Text>
          </TouchableOpacity>
        </Stack>
      </Stack>
    );
  },
);

const useStyles = createStyles(({ scale }) => ({
  iconWrap: {
    width: scale(78),
    height: scale(78),
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'white',
  },
  pinImage: {
    width: scale(78),
    height: scale(78),
  },
  nextBtn: {
    alignSelf: 'flex-start',
    width: scale(91),
    height: scale(24),
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: scale(4),
    ...(Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 2,
      },
      android: { elevation: 2 },
    }) ?? {}),
  },
  nextBtnText: {
    color: 'white',
    letterSpacing: 0.2,
  },
}));
